// src/pages/CasesList.js
import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { deleteCaseIfNoKits } from "../utils/caseHelpers";
import { duplicateCase } from "../utils/caseHelpers";
import toast from "react-hot-toast";

export default function CasesList() {
	const [cases, setCases] = useState([]);
	const [loading, setLoading] = useState(true);
	const { role } = useAuth();
	const [selectedKitFilters, setSelectedKitFilters] = useState([]);
	const [availableKits, setAvailableKits] = useState([]);
	const [kitCaseCounts, setKitCaseCounts] = useState({});
	const [unassignedCount, setUnassignedCount] = useState(0);
	const [openMenuId, setOpenMenuId] = useState(null);
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const navigate = useNavigate();

	function toggleMenu(caseId) {
		setOpenMenuId((prev) => (prev === caseId ? null : caseId));
	}

	const toggleSidebar = () => {
		setIsSidebarOpen((prevState) => !prevState);
	};

	// Close the sidebar when the overlay is clicked
	const closeSidebar = () => {
		setIsSidebarOpen(false);
	};

	useEffect(() => {
		function handleClickOutside(e) {
			if (!e.target.closest(".list-item__actions")) setOpenMenuId(null);
		}
		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, []);

	useEffect(() => {
		fetchCases();
	}, []);

	async function fetchCases() {
		setLoading(true);

		const [
			{ data: cases, error: casesError },
			{ data: gearStatuses, error: gearError },
		] = await Promise.all([
			supabase
				.from("cases")
				.select(
					`id, name, weight, notes, kit_cases (kit_id, kits (id, name, color))`
				),
			supabase
				.from("gear")
				.select(`case_id, gear_statuses(label)`)
				.in("gear_statuses.label", [
					"Missing",
					"Needs Repair",
					"In Use",
				]),
		]);

		if (casesError || gearError) {
			console.error("Error fetching data:", casesError || gearError);
			setLoading(false);
			return;
		}

		// Track statuses per case
		const statusMap = {}; // case_id -> { missing: bool, needsRepair: bool }

		gearStatuses.forEach((gear) => {
			const caseId = gear.case_id;
			const label = gear.gear_statuses?.label;

			if (!statusMap[caseId]) {
				statusMap[caseId] = {
					missing: false,
					needsRepair: false,
					inUse: false,
				};
			}

			if (label === "Missing") statusMap[caseId].missing = true;
			if (label === "Needs Repair") statusMap[caseId].needsRepair = true;
			if (label === "In Use") statusMap[caseId].inUse = true;
		});

		// Normalize kits into each case
		const formatted = cases.map((c) => ({
			...c,
			kits: c.kit_cases?.map((kc) => kc.kits).filter(Boolean) || [],
			statusFlags: statusMap[c.id] || {
				missing: false,
				needsRepair: false,
				inUse: false,
			},
		}));

		// Sort alphabetically
		formatted.sort((a, b) =>
			a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
		);

		setCases(formatted);

		// Count kits
		const counts = {};
		let unassigned = 0;
		formatted.forEach((c) => {
			if (c.kits.length === 0) {
				unassigned++;
			} else {
				c.kits.forEach((kit) => {
					counts[kit.id] = (counts[kit.id] || 0) + 1;
				});
			}
		});

		setKitCaseCounts(counts);
		setUnassignedCount(unassigned);

		const uniqueKits = [];
		const seenKitIds = new Set();
		formatted.forEach((c) => {
			c.kits.forEach((kit) => {
				if (!seenKitIds.has(kit.id)) {
					seenKitIds.add(kit.id);
					uniqueKits.push(kit);
				}
			});
		});

		uniqueKits.sort((a, b) =>
			a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
		);

		setAvailableKits(uniqueKits);
		setLoading(false);
	}

	async function handleDeleteCase(caseId) {
		const result = await deleteCaseIfNoKits(caseId, { dryRun: true });

		if (!result.success) {
			toast.error(result.message);
			return;
		}

		if (!window.confirm("Are you sure you want to delete this case?"))
			return;

		const toastId = toast.loading("Deleting case...");
		const deleteResult = await deleteCaseIfNoKits(caseId);

		if (!deleteResult.success) {
			toast.error(deleteResult.message, { id: toastId });
		} else {
			toast.success("Case deleted successfully.", { id: toastId });
			await fetchCases();
			setOpenMenuId(null);
		}
	}

	async function handleDuplicateCase(caseId) {
		await duplicateCase(caseId, navigate);
	}

	function handleKitFilterChange(kitId) {
		setSelectedKitFilters((prev) =>
			prev.includes(kitId)
				? prev.filter((id) => id !== kitId)
				: [...prev, kitId]
		);
	}

	function handleUnassignedToggle() {
		setSelectedKitFilters((prev) =>
			prev.includes("unassigned")
				? prev.filter((id) => id !== "unassigned")
				: [...prev, "unassigned"]
		);
	}

	const filteredCases = cases.filter((c) => {
		const kitIds = c.kits.map((k) => k.id);

		if (selectedKitFilters.includes("unassigned") && kitIds.length === 0) {
			return true;
		}

		if (selectedKitFilters.some((filterId) => kitIds.includes(filterId))) {
			return true;
		}

		// If no filters are selected, show all
		return selectedKitFilters.length === 0;
	});

	if (loading)
		return (
			<div className="loading">
				<div>
					<i className="pi pi-spin pi-spinner"></i>
				</div>
			</div>
		);

	return (
		<div className="cases-list">
			<div
				className={`overlay ${isSidebarOpen ? "active" : ""}`}
				onClick={closeSidebar}
			></div>

			<div className={`sidebar ${isSidebarOpen ? "open" : ""}`}>
				<div className="kit-filters">
					<strong>Filter by Kit</strong>
					<button
						onClick={toggleSidebar}
						className="button-sidebar-close"
					>
						<i className="pi pi-times"></i>
					</button>
					<ul>
						<li>
							<label className="custom-checkbox">
								<input
									type="checkbox"
									checked={selectedKitFilters.includes(
										"unassigned"
									)}
									onChange={handleUnassignedToggle}
								/>
								<span className="checkbox-icon checkmark">
									{selectedKitFilters.includes(
										"unassigned"
									) && <i className="pi pi-check" />}
								</span>
								Unassigned
								<span className="kit-filter__count">
									({unassignedCount})
								</span>
							</label>
						</li>
						{availableKits.map((kit) => (
							<li key={kit.id}>
								<label className="custom-checkbox">
									<input
										type="checkbox"
										checked={selectedKitFilters.includes(
											kit.id
										)}
										onChange={() =>
											handleKitFilterChange(kit.id)
										}
									/>
									<span className="checkbox-icon checkmark">
										{selectedKitFilters.includes(
											kit.id
										) && <i className="pi pi-check" />}
									</span>
									{kit.color && (
										<span
											className="kit-filter__color"
											style={{
												backgroundColor: kit.color,
											}}
										></span>
									)}
									{kit.name}
									{kitCaseCounts[kit.id] && (
										<span className="kit-filter__count">
											({kitCaseCounts[kit.id]})
										</span>
									)}
								</label>
							</li>
						))}
					</ul>
				</div>
			</div>
			<div className="main">
				<div className="action-lead">
					<h1>Cases</h1>
					<div className="action-lead__buttons">
						<button
							className="button action-toggle"
							onClick={toggleSidebar}
						>
							<i className="pi pi-filter"></i>
							Filter
						</button>
						{role === "admin" && (
							<Link to="/add-case" className="button green">
								<i className="pi pi-plus"></i>
								Add Case
							</Link>
						)}
					</div>
				</div>
				{selectedKitFilters.length > 0 && (
					<div className="filter-message">
						Showing cases associated with{" "}
						{selectedKitFilters.map((id, index) => {
							const kit =
								id === "unassigned"
									? { name: "Unassigned", color: null }
									: availableKits.find(
											(k) => k.id === id
									  ) || {
											name: "Unknown",
											color: null,
									  };

							return (
								<span key={id} className="filter-label">
									{index > 0 && ", "}
									<span>
										{kit.color && (
											<span
												className="filter-message__dot"
												style={{
													backgroundColor:
														kit.color || "#eee",
												}}
											></span>
										)}
										{kit.name}
									</span>
								</span>
							);
						})}
					</div>
				)}

				<div className="cases-list__list-wrapper">
					{cases.length === 0 ? (
						<p>No cases found.</p>
					) : (
						filteredCases.map((c) => (
							<div key={c.id} className="list-item">
								<div className="list-item__left">
									<div className="list-item__name-row">
										<Link
											to={`/cases/${c.id}`}
											className="list-item__name"
										>
											{c.name}
										</Link>
										{c.statusFlags?.missing && (
											<span className="list-item__icon-wrapper missing">
												<i
													className="pi pi-exclamation-triangle"
													title="Missing gear"
												></i>
											</span>
										)}
										{c.statusFlags?.needsRepair && (
											<span className="list-item__icon-wrapper repair">
												<i
													className="pi pi-wrench"
													title="Needs repair"
												></i>
											</span>
										)}
										{c.statusFlags?.inUse && (
											<span className="list-item__icon-wrapper inuse">
												<i
													className="pi pi-ban"
													title="In Use"
												></i>
											</span>
										)}
										{c.weight && (
											<span className="list-item__case-weight">
												({c.weight} lbs)
											</span>
										)}
									</div>

									{c.kits.length > 0 && (
										<div className="pill-wrapper">
											{c.kits.map((kit) => (
												<span
													key={kit.id}
													className="pill"
													style={{
														cursor: "pointer",
													}}
													onClick={() =>
														handleKitFilterChange(
															kit.id
														)
													}
													title={`Filter by ${kit.name}`}
												>
													{kit.color && (
														<span
															style={{
																backgroundColor:
																	kit.color,
															}}
															className="color"
														></span>
													)}
													{kit.name}
												</span>
											))}
										</div>
									)}
								</div>
								{role === "admin" && (
									<div className="list-item__actions">
										<button
											className="three-dot-button no-text"
											onClick={() => toggleMenu(c.id)}
										>
											<i className="pi pi-ellipsis-v"></i>
										</button>
										{openMenuId === c.id && (
											<ul className="list-item__actions-menu">
												<li
													onClick={() =>
														handleDuplicateCase(
															c.id
														)
													}
													className="list-item__action-duplicate"
												>
													<i className="pi pi-clone"></i>
													Duplicate
												</li>
												<li
													onClick={() =>
														handleDeleteCase(c.id)
													}
													className="list-item__action-delete"
												>
													<i className="pi pi-trash"></i>
													Delete
												</li>
											</ul>
										)}
									</div>
								)}
							</div>
						))
					)}
				</div>
			</div>
		</div>
	);
}
